import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';

import AddIcon from '@atlaskit/icon/core/add';
import AiGenerativeTextSummaryIcon from '@atlaskit/icon/core/ai-generative-text-summary';
import AttachmentIcon from '@atlaskit/icon/core/attachment';
import DatabaseIcon from '@atlaskit/icon/core/database';
import GridIcon from '@atlaskit/icon/core/grid';
import LockLockedIcon from '@atlaskit/icon/core/lock-locked';
import MagicWandIcon from '@atlaskit/icon/core/magic-wand';
import ProjectStatusIcon from '@atlaskit/icon/core/project-status';
import ShapesIcon from '@atlaskit/icon/core/shapes';
import AssetsDataManagerIcon from '@atlaskit/icon-lab/core/assets-data-manager';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import TextRephraseIcon from '@atlaskit/icon-lab/core/text-rephrase';

import { type ConversationAction, HandleActionType } from '@atlassian/rovo-conversation-actions';

import { useCreateSpaceStoreActions } from '@confluence/create-space/entry-points/createSpace.ts';
import { useSmartCreationModalActions } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import {
	useContentType as useContentTypeExternal,
	usePageSpaceKey,
} from '@confluence/page-context';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';

import { messages } from '../messages.ts';

import { useRecentJiraProjectAndIssue } from './useRecentJiraProjectAndIssue.tsx';

type Greeting = {
	title: string;
	subtitle: string;
};

export type OnboardingRovoConfig = {
	actions: ConversationAction[];
	greeting: Greeting;
};

type UseOnboardingRovoConfigParams = {
	/** When true, the hook short-circuits and returns null. */
	skip: boolean;
	/** Whether the user skipped AIFC during onboarding */
	skippedAifc: boolean;
};

export type OnboardingContentType = 'page' | 'whiteboard' | 'database' | 'liveDoc';

const useContentType = (): OnboardingContentType => {
	const [contentType] = useContentTypeExternal();
	const isLivePage = useIsCurrentPageLive();
	if (contentType === 'whiteboard' || contentType === 'database') {
		return contentType;
	}
	return isLivePage ? 'liveDoc' : 'page';
};

/**
 * Returns an `OnboardingRovoConfig` when the onboarding entry point is active,
 * or `null` when `skip` is true (i.e. no entry point was detected).
 */
export function useOnboardingRovoConfig({
	skip,
	skippedAifc,
}: UseOnboardingRovoConfigParams): OnboardingRovoConfig | null {
	const currentContentType = useContentType();
	const {
		data: { hasFullData: isXflowUser, issue, project },
		isLoading,
	} = useRecentJiraProjectAndIssue({ skip });
	const { formatMessage } = useIntl();

	const { openModal: openCwrModal } = useSmartCreationModalActions();

	const { openDrawer: openCreateSpaceDrawer } = useCreateSpaceStoreActions();

	const [pageSpaceKey] = usePageSpaceKey();

	const openCwrCreateNew = useCallback(() => {
		openCwrModal({ entryPoint: 'rovo-onboarding', spaceKey: pageSpaceKey ?? '' });
	}, [openCwrModal, pageSpaceKey]);

	const openCreateSpace = useCallback(() => {
		openCreateSpaceDrawer({ current: null }, undefined);
	}, [openCreateSpaceDrawer]);

	const currentContentTypeLabel = useMemo(() => {
		switch (currentContentType) {
			case 'whiteboard':
				return formatMessage(messages.contentTypeWhiteboard);
			case 'database':
				return formatMessage(messages.contentTypeDatabase);
			case 'liveDoc':
			case 'page':
			default:
				return formatMessage(messages.contentTypeDoc);
		}
	}, [currentContentType, formatMessage]);

	const outputContentTypeLabel = useMemo(() => {
		switch (currentContentType) {
			case 'whiteboard':
			case 'database':
				return formatMessage(messages.contentTypeDoc);
			case 'liveDoc':
			case 'page':
			default:
				return formatMessage(messages.contentTypeWhiteboard);
		}
	}, [currentContentType, formatMessage]);

	const landSkippedAifcConfig = useMemo<OnboardingRovoConfig>(
		() => ({
			greeting: {
				title: formatMessage(messages.welcomeTitle),
				subtitle: formatMessage(messages.welcomeSubtitle),
			},
			actions: [
				{
					id: 'rovo-onboarding-start-creating',
					title: formatMessage(messages.intentStartCreatingLabel),
					secondaryDescription: formatMessage(messages.intentStartCreatingSubtitle),
					alwaysShowSecondaryText: true,
					icon: () => <AddIcon label="" />,
					children: [
						{
							id: 'rovo-onboarding-create-something-new',
							title: formatMessage(messages.createSomethingNew),
							alwaysShowSecondaryText: true,
							icon: () => <MagicWandIcon label="" />,
							onClick: openCwrCreateNew,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: 'rovo-onboarding-create-from-file',
							title: formatMessage(messages.createFromFile),
							alwaysShowSecondaryText: true,
							icon: () => <AttachmentIcon label="" />,
							onClick: () => {
								// TODO: wire up create-from-file flow
							},
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: 'rovo-onboarding-create-from-template',
							title: formatMessage(messages.createFromTemplate),
							alwaysShowSecondaryText: true,
							icon: () => <TemplateIcon label="" />,
							onClick: () => window.open('/wiki/templates', '_blank', 'noopener,noreferrer'),

							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
				{
					id: 'rovo-onboarding-set-up-space',
					title: formatMessage(messages.intentSetUpSpaceLabel),
					secondaryDescription: formatMessage(messages.intentSetUpSpaceSubtitle),
					alwaysShowSecondaryText: true,
					icon: () => <GridIcon label="" />,
					children: [
						{
							id: 'rovo-onboarding-create-space',
							title: formatMessage(messages.createSpace),
							alwaysShowSecondaryText: true,
							icon: () => <GridIcon label="" />,
							onClick: openCreateSpace,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: 'rovo-onboarding-set-up-site-permissions',
							title: formatMessage(messages.setUpSitePermissions),
							alwaysShowSecondaryText: true,
							icon: () => <LockLockedIcon label="" />,
							onClick: () =>
								window.open('/wiki/admin/permissions/global', '_blank', 'noopener,noreferrer'),
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: 'rovo-onboarding-connect-your-tools',
							title: formatMessage(messages.connectYourTools),
							alwaysShowSecondaryText: true,
							icon: () => <AssetsDataManagerIcon label="" />,
							onClick: () =>
								window.open('/wiki/admin/confluence-apps', '_blank', 'noopener,noreferrer'),
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
			],
		}),
		[formatMessage, openCwrCreateNew, openCreateSpace],
	);

	const landCompletedAifcConfig = useMemo<OnboardingRovoConfig>(
		() => ({
			greeting: {
				title: formatMessage(messages.nicelyDoneTitle),
				subtitle: formatMessage(messages.nicelyDoneSubtitle),
			},
			actions: [
				{
					id: 'rovo-onboarding-keep-momentum',
					title: formatMessage(messages.intentKeepMomentumLabel),
					secondaryDescription: formatMessage(messages.intentKeepMomentumSubtitle),
					alwaysShowSecondaryText: true,
					icon: () => <AddIcon label="" />,
					children: [
						{
							id: 'rovo-onboarding-turn-content-into-content',
							title: formatMessage(messages.turnContentTypeIntoContentType, {
								sourceContentType: currentContentTypeLabel,
								outputContentType: outputContentTypeLabel,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <ShapesIcon label="" />,
							staticPrompt: formatMessage(messages.turnContentTypeIntoContentType, {
								sourceContentType: currentContentTypeLabel,
								outputContentType: outputContentTypeLabel,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id: 'rovo-onboarding-turn-into-action-items',
							title: formatMessage(messages.turnContentTypeIntoActionItems, {
								contentType: currentContentTypeLabel,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <AiGenerativeTextSummaryIcon label="" />,
							staticPrompt: formatMessage(messages.turnContentTypeIntoActionItems, {
								contentType: currentContentTypeLabel,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id: 'rovo-onboarding-create-something-new',
							title: formatMessage(messages.createSomethingNew),
							alwaysShowSecondaryText: true,
							icon: () => <MagicWandIcon label="" />,
							onClick: openCwrCreateNew,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
				{
					id: 'rovo-onboarding-set-up-space',
					title: formatMessage(messages.intentSetUpSpaceLabel),
					secondaryDescription: formatMessage(messages.intentSetUpSpaceSubtitle),
					alwaysShowSecondaryText: true,
					icon: () => <GridIcon label="" />,
					children: [
						{
							id: 'rovo-onboarding-create-space',
							title: formatMessage(messages.createSpace),
							alwaysShowSecondaryText: true,
							icon: () => <GridIcon label="" />,
							onClick: openCreateSpace,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: 'rovo-onboarding-set-up-site-permissions',
							title: formatMessage(messages.setUpSitePermissions),
							alwaysShowSecondaryText: true,
							icon: () => <LockLockedIcon label="" />,
							onClick: () =>
								window.open('/wiki/admin/permissions/global', '_blank', 'noopener,noreferrer'),
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: 'rovo-onboarding-connect-your-tools',
							title: formatMessage(messages.connectYourTools),
							alwaysShowSecondaryText: true,
							icon: () => <AssetsDataManagerIcon label="" />,
							onClick: () =>
								window.open('/wiki/admin/confluence-apps', '_blank', 'noopener,noreferrer'),
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
			],
		}),
		[
			currentContentTypeLabel,
			formatMessage,
			openCreateSpace,
			openCwrCreateNew,
			outputContentTypeLabel,
		],
	);

	const xflowCompletedAifcConfig = useMemo<OnboardingRovoConfig>(
		() => ({
			greeting: {
				title: formatMessage(messages.nicelyDoneTitle),
				subtitle: formatMessage(messages.nicelyDoneSubtitle),
			},
			actions: [
				{
					id: 'rovo-onboarding-keep-momentum',
					title: formatMessage(messages.intentKeepMomentumLabel),
					secondaryDescription: formatMessage(messages.intentKeepMomentumSubtitle),
					alwaysShowSecondaryText: true,
					icon: () => <AddIcon label="" />,
					children: [
						{
							id: 'rovo-onboarding-turn-content-into-content',
							title: formatMessage(messages.turnContentTypeIntoContentType, {
								sourceContentType: currentContentTypeLabel,
								outputContentType: outputContentTypeLabel,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <ShapesIcon label="" />,
							staticPrompt: formatMessage(messages.turnContentTypeIntoContentType, {
								sourceContentType: currentContentTypeLabel,
								outputContentType: outputContentTypeLabel,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id:
								issue.issueTypeName === 'epic'
									? 'rovo-onboarding-create-scope-doc'
									: 'rovo-onboarding-break-down-issue',
							title: formatMessage(
								issue.issueTypeName === 'epic' ? messages.createScopeDoc : messages.breakDownIssue,
								{
									issueKey: issue.key,
									issueSummary: issue.summary,
								},
							),
							alwaysShowSecondaryText: true,
							icon: () => <TextRephraseIcon label="" />,
							staticPrompt: formatMessage(
								issue.issueTypeName === 'epic' ? messages.createScopeDoc : messages.breakDownIssue,
								{
									issueKey: issue.key,
									issueSummary: issue.summary,
								},
							),
							handleType: HandleActionType.Chat,
						},
						{
							id: 'rovo-onboarding-create-something-new',
							title: formatMessage(messages.createSomethingNew),
							alwaysShowSecondaryText: true,
							icon: () => <MagicWandIcon label="" />,
							onClick: openCwrCreateNew,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
				{
					id: 'rovo-onboarding-set-up-space',
					title: formatMessage(messages.intentSetUpSpaceLabel),
					secondaryDescription: formatMessage(messages.intentSetUpSpaceSubtitle),
					alwaysShowSecondaryText: true,
					icon: () => <GridIcon label="" />,
					children: [
						{
							id: 'rovo-onboarding-build-knowledge-hub',
							title: formatMessage(messages.buildKnowledgeHub, {
								projectName: project.name,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <DatabaseIcon label="" />,
							staticPrompt: formatMessage(messages.buildKnowledgeHub, {
								projectName: project.name,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id: 'rovo-onboarding-set-up-site-permissions',
							title: formatMessage(messages.setUpSitePermissions),
							alwaysShowSecondaryText: true,
							icon: () => <LockLockedIcon label="" />,
							onClick: () =>
								window.open('/wiki/admin/permissions/global', '_blank', 'noopener,noreferrer'),
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: 'rovo-onboarding-connect-your-tools',
							title: formatMessage(messages.connectYourTools),
							alwaysShowSecondaryText: true,
							icon: () => <AssetsDataManagerIcon label="" />,
							onClick: () =>
								window.open('/wiki/admin/confluence-apps', '_blank', 'noopener,noreferrer'),
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
			],
		}),
		[
			currentContentTypeLabel,
			formatMessage,
			issue.issueTypeName,
			issue.key,
			issue.summary,
			openCwrCreateNew,
			outputContentTypeLabel,
			project.name,
		],
	);

	const xflowSkippedAifcConfig = useMemo<OnboardingRovoConfig>(
		() => ({
			greeting: {
				title: formatMessage(messages.welcomeTitle),
				subtitle: formatMessage(messages.welcomeSubtitle),
			},
			actions: [
				{
					id: 'rovo-onboarding-start-creating',
					title: formatMessage(messages.intentStartCreatingLabel),
					secondaryDescription: formatMessage(messages.intentStartCreatingSubtitle),
					alwaysShowSecondaryText: true,
					icon: () => <AddIcon label="" />,
					children: [
						{
							id: 'rovo-onboarding-write-status-update',
							title: formatMessage(messages.writeStatusUpdate, {
								projectName: project.name,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <ProjectStatusIcon label="" />,
							staticPrompt: formatMessage(messages.writeStatusUpdate, {
								projectName: project.name,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id: 'rovo-onboarding-break-down-issue',
							title: formatMessage(messages.breakDownIssue, {
								issueKey: issue.key,
								issueSummary: issue.summary,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <TextRephraseIcon label="" />,
							staticPrompt: formatMessage(messages.breakDownIssue, {
								issueKey: issue.key,
								issueSummary: issue.summary,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id: 'rovo-onboarding-create-something-new',
							title: formatMessage(messages.createSomethingNew),
							alwaysShowSecondaryText: true,
							icon: () => <MagicWandIcon label="" />,
							onClick: openCwrCreateNew,
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
				{
					id: 'rovo-onboarding-set-up-space',
					title: formatMessage(messages.intentSetUpSpaceLabel),
					secondaryDescription: formatMessage(messages.intentSetUpSpaceSubtitle),
					alwaysShowSecondaryText: true,
					icon: () => <GridIcon label="" />,
					children: [
						{
							id: 'rovo-onboarding-build-knowledge-hub',
							title: formatMessage(messages.buildKnowledgeHub, {
								projectName: project.name,
							}),
							alwaysShowSecondaryText: true,
							icon: () => <DatabaseIcon label="" />,
							staticPrompt: formatMessage(messages.buildKnowledgeHub, {
								projectName: project.name,
							}),
							handleType: HandleActionType.Chat,
						},
						{
							id: 'rovo-onboarding-set-up-site-permissions',
							title: formatMessage(messages.setUpSitePermissions),
							alwaysShowSecondaryText: true,
							icon: () => <LockLockedIcon label="" />,
							onClick: () =>
								window.open('/wiki/admin/permissions/global', '_blank', 'noopener,noreferrer'),
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
						{
							id: 'rovo-onboarding-connect-your-tools',
							title: formatMessage(messages.connectYourTools),
							alwaysShowSecondaryText: true,
							icon: () => <AssetsDataManagerIcon label="" />,
							onClick: () =>
								window.open('/wiki/admin/confluence-apps', '_blank', 'noopener,noreferrer'),
							type: 'ai-action',
							handleType: HandleActionType.Base,
						},
					],
					handleType: HandleActionType.Nested,
					type: 'ai-action',
				},
			],
		}),
		[formatMessage, issue.key, issue.summary, openCwrCreateNew, project.name],
	);

	if (skip || isLoading) {
		return null;
	}

	if (isXflowUser) {
		return skippedAifc ? xflowSkippedAifcConfig : xflowCompletedAifcConfig;
	}

	return skippedAifc ? landSkippedAifcConfig : landCompletedAifcConfig;
}
